import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { TEXT_SLOTS } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TextsPanel = ({ auth }) => {
    const [stored, setStored] = useState({});
    const [drafts, setDrafts] = useState({});
    const [busy, setBusy] = useState("");

    const load = async () => {
        try {
            const r = await axios.get(`${API}/texts`);
            setStored(r.data.texts || {});
            setDrafts(r.data.texts || {});
        } catch {
            /* sem ligação */
        }
    };

    useEffect(() => {
        load();
    }, []);

    const save = async (key) => {
        setBusy(key);
        try {
            await axios.put(`${API}/admin/texts/${key}`, { value: drafts[key] ?? "" }, auth());
            await load();
            toast.success("Texto atualizado no site.");
        } catch (err) {
            toast.error(
                err.response?.status === 401
                    ? "Sessão expirada. Inicie sessão novamente."
                    : "Falha ao guardar o texto.",
            );
        } finally {
            setBusy("");
        }
    };

    const reset = async (key) => {
        setBusy(key);
        try {
            await axios.delete(`${API}/admin/texts/${key}`, auth());
            await load();
            toast.success("Texto reposto para o original.");
        } catch {
            toast.error("Falha ao repor o texto.");
        } finally {
            setBusy("");
        }
    };

    const groups = [...new Set(TEXT_SLOTS.map((s) => s.group))];

    return (
        <div className="mt-14" data-testid="texts-panel">
            <p className="max-w-2xl text-sm text-zinc-400">
                Edite títulos e descrições do site. Nos títulos, escreva{" "}
                <span className="font-mono text-brand">|</span> antes da parte que
                deve aparecer a vermelho (ex.: "Soluções de transporte |à
                medida."). O botão "Repor" volta ao texto original.
            </p>
            {groups.map((g) => (
                <div key={g} className="mt-12">
                    <h2 className="font-mono text-xs uppercase tracking-[0.35em] text-zinc-500">
                        {g}
                    </h2>
                    <div className="mt-6 space-y-5">
                        {TEXT_SLOTS.filter((s) => s.group === g).map((s) => {
                            const custom = stored[s.key] !== undefined;
                            const val =
                                drafts[s.key] !== undefined ? drafts[s.key] : s.fallback;
                            const dirty = val !== (stored[s.key] ?? s.fallback);
                            return (
                                <div
                                    key={s.key}
                                    className="border border-white/10 bg-coal p-5"
                                    data-testid={`text-slot-${s.key}`}
                                >
                                    <div className="flex items-center justify-between gap-4">
                                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
                                            {s.label}
                                        </p>
                                        {custom && (
                                            <span className="bg-brand px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-white">
                                                Editado
                                            </span>
                                        )}
                                    </div>
                                    <textarea
                                        rows={2}
                                        value={val}
                                        onChange={(e) =>
                                            setDrafts({ ...drafts, [s.key]: e.target.value })
                                        }
                                        className="mt-3 w-full resize-y border border-white/15 bg-steel px-3 py-2.5 text-sm text-white outline-none focus:border-brand"
                                        data-testid={`text-input-${s.key}`}
                                    />
                                    <div className="mt-3 flex gap-2">
                                        <button
                                            onClick={() => save(s.key)}
                                            disabled={!dirty || busy === s.key}
                                            data-testid={`text-save-${s.key}`}
                                            className="inline-flex items-center gap-2 bg-brand px-5 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-ink disabled:opacity-40"
                                        >
                                            {busy === s.key && (
                                                <Loader2 className="h-3 w-3 animate-spin" />
                                            )}
                                            Guardar
                                        </button>
                                        {custom && (
                                            <button
                                                onClick={() => reset(s.key)}
                                                disabled={busy === s.key}
                                                data-testid={`text-reset-${s.key}`}
                                                className="border border-white/20 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:border-brand hover:text-brand"
                                            >
                                                Repor
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};
